"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import Script from "next/script";
import { SiteHeader } from "@/components/layout/site-header";
import { SiteFooter } from "@/components/layout/site-footer";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";

export default function ContactPage() {
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (
      !scriptReady ||
      !widgetRef.current ||
      !window.turnstile ||
      !SITE_KEY ||
      widgetId.current
    ) {
      return;
    }
    widgetId.current = window.turnstile.render(widgetRef.current, {
      sitekey: SITE_KEY,
      callback: (t) => setToken(t),
      "expired-callback": () => setToken(null),
      "error-callback": () => setToken(null),
      theme: "light",
    });
  }, [scriptReady]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setStatus("idle");
    setErrorMsg(null);

    const res = await fetch("/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, subject, message, token }),
    });

    if (res.ok) {
      setStatus("ok");
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      setToken(null);
      if (widgetId.current && window.turnstile) {
        window.turnstile.reset(widgetId.current);
      }
    } else {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setErrorMsg(body.error ?? "Erreur lors de l'envoi");
      setStatus("error");
    }
    setSubmitting(false);
  }

  return (
    <>
      {SITE_KEY && (
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js"
          strategy="afterInteractive"
          onLoad={() => setScriptReady(true)}
        />
      )}
      <SiteHeader />
      <main className="max-w-2xl mx-auto px-6 py-16 md:py-24">
        <h1 className="font-headline text-display-md text-garden-800 mb-4">
          Nous écrire
        </h1>
        <p className="text-body-lg text-on-surface-variant mb-10">
          Une question, un bug, une idée ? Le formulaire est lu et traité dans
          la semaine.
        </p>

        {status === "ok" ? (
          <div className="card-elevated p-8 text-center space-y-3">
            <div className="text-4xl">🌱</div>
            <h2 className="font-headline text-headline-md text-garden-700">
              Message reçu
            </h2>
            <p className="text-body-md text-on-surface-variant">
              Merci ! Nous vous répondons dès que possible à l&apos;adresse
              indiquée.
            </p>
          </div>
        ) : !SITE_KEY ? (
          <div className="card-elevated p-8 text-center space-y-3">
            <h2 className="font-headline text-headline-sm text-garden-700">
              Formulaire indisponible
            </h2>
            <p className="text-body-md text-on-surface-variant">
              Le formulaire de contact n&apos;est pas encore configuré côté
              serveur. Veuillez réessayer plus tard.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field
                label="Nom"
                value={name}
                onChange={setName}
                required
                autoComplete="name"
              />
              <Field
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                required
                autoComplete="email"
              />
            </div>

            <Field
              label="Sujet"
              value={subject}
              onChange={setSubject}
              required
            />

            <div className="space-y-1.5">
              <label
                htmlFor="message"
                className="block text-label-md text-on-surface-variant"
              >
                Message
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                required
                minLength={10}
                maxLength={5000}
                className="w-full px-4 py-3 rounded-lg bg-surface-container-lowest text-body-md text-on-surface outline-none resize-y focus:ring-1 focus:ring-primary"
              />
            </div>

            <div ref={widgetRef} className="flex justify-center" />

            {status === "error" && (
              <p className="text-label-md text-error bg-error-container/30 rounded-lg px-3 py-2">
                {errorMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={!token || submitting}
              className="bg-primary text-on-primary px-8 py-3.5 rounded-xl text-label-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Envoi…" : "Envoyer"}
            </button>
          </form>
        )}
      </main>
      <SiteFooter />
    </>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-label-md text-on-surface-variant">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        autoComplete={autoComplete}
        className="w-full px-4 py-3 rounded-lg bg-surface-container-lowest text-body-md text-on-surface outline-none focus:ring-1 focus:ring-primary"
      />
    </div>
  );
}
